import { apiClient } from "../../../lib";
import { RagStatus } from "../components/RagMarker";

export interface CoamAreaRagStatus {
  time: RagStatus;
  cost: RagStatus;
  quality: RagStatus;
}

export interface CoamAreaProperties {
  id: number;
  name: string;
  boundryType: string;
  gsscode: string | null;
  lastUpdatedDatetime: string;
  longitudeCentroid: string;
  latitudeCentroid: string;
  ragStatus?: CoamAreaRagStatus;
}

export interface CoamAreaFeature {
  type: "Feature";
  geometry: {
    type: "Polygon" | "MultiPolygon";
    coordinates: number[][][] | number[][][][];
  };
  properties: CoamAreaProperties;
}

export interface CoamAreaResponse {
  status: boolean;
  http_code: number;
  message: string;
  response_time: string;
  meta_data: unknown;
  data: {
    type: "FeatureCollection";
    features: CoamAreaFeature[];
  };
}

interface RegionApiResponse {
  status: boolean;
  http_code: number;
  message: string;
  response_time: string;
  meta_data: unknown;
  data: Array<{
    id: number;
    region_name: string;
    code: string;
    asset_id: number;
    type: string;
    updated_date: string;
    updated_by: string;
    geometry: {
      type: "Point";
      coordinates: [number, number];
    };
    location_geometry: {
      type: "Polygon" | "MultiPolygon";
      coordinates: number[][][] | number[][][][];
    };
    rag: {
      time: RagStatus;
      cost: RagStatus;
      quality: RagStatus;
      overall: RagStatus;
    };
  }>;
}

const transformRegionDataToGeoJSON = (
  apiData: RegionApiResponse
): CoamAreaResponse => {
  const features: CoamAreaFeature[] = apiData.data.map((region) => {
    const [longitude, latitude] = region.geometry.coordinates;

    return {
      type: "Feature",
      geometry: region.location_geometry,
      properties: {
        id: region.id,
        name: region.region_name,
        boundryType: region.type,
        gsscode: region.code,
        lastUpdatedDatetime: region.updated_date,
        longitudeCentroid: longitude.toString(),
        latitudeCentroid: latitude.toString(),
        ragStatus: {
          time: region.rag.time,
          cost: region.rag.cost,
          quality: region.rag.quality,
        },
      },
    };
  });

  return {
    status: apiData.status,
    http_code: apiData.http_code,
    message: apiData.message,
    response_time: apiData.response_time,
    meta_data: apiData.meta_data,
    data: {
      type: "FeatureCollection",
      features,
    },
  };
};

export interface StageGatePhase {
  id: number;
  name: string;
  description: string;
  project_count: number;
  sequence_order: number;
}

export interface QuickFilterResponse {
  status: boolean;
  http_code: number;
  message: string;
  response_time: string;
  meta_data: unknown;
  data: StageGatePhase[];
}

export interface WorkstackBreakdown {
  programme: string;
  count: string;
}

export interface WorkstreamBreakdown {
  workstream: string;
  count: string;
}

export interface WorkstackData<T = WorkstackBreakdown> {
  summary: {
    total: string;
    thisYear: string;
    nextYear: string;
    completed: string;
  };
  breakdown: {
    total: T[];
    thisYear: T[];
    nextYear: T[];
    completed: T[];
  };
}

export interface WorkstackResponse<T = WorkstackBreakdown> {
  status: boolean;
  http_code: number;
  message: string;
  response_time: string;
  meta_data: string;
  data: WorkstackData<T>;
}

export interface ProjectPhase {
  phase_id: number;
  name: string;
  project_id: number;
  display_order: number;
  status: string;
  percentage_complete: number;
}

export interface ProjectRag {
  time: "green" | "red" | "amber";
  cost: "green" | "red" | "amber";
  quality: "green" | "red" | "amber";
  overall: "green" | "red" | "amber";
}

export interface ProjectFinance {
  total_budget: number;
  total_spent: number;
  total_remaining: number;
}

export interface ProjectProgress {
  total: number;
  percentage: number;
}

export interface Project {
  work_type_id: number | null;
  programme_id: number;
  portfolio_id: number | null;
  org_id: number;
  region_id: number;
  workstream_id: number;
  wbs_code_id: number | null;
  iln_number_id: number | null;
  owner_id: number | null;
  site_id: number;
  created_date: string;
  updated_at: string;
  project_budget: number;
  planned_start_date: string;
  planned_commissioning_date: string;
  planned_close_up_date: string;
  project_stake_holders_id: number | null;
  county_id: number;
  area_id: number;
  project_reference: string;
  title: string;
  id: number;
  description: string;
  functional_location: string | null;
  project_scope: string;
  county: string;
  address: string;
  area_text: string;
  site: string;
  portfolio: string;
  coordinates: {
    type: "point";
    coordinates: [number, number];
  };
  what3_words: string;
  phase_template: string;
  hcode: string;
  project_status: string;
  programme: string;
  created_by: number | null;
  workstream: string;
  updated_by: number | null;
  actual_spend: number | null;
  actual_end_date: string | null;
  network: string;
  planned_end_date: string;
  sequence_order: number;
  status: string;
  phase_name: string;
  is_favourite: boolean | null;
  phases: ProjectPhase[];
  progress: ProjectProgress;
  rag: ProjectRag;
  finance: ProjectFinance;
  is_fav: boolean;
}

export interface ProjectsResponse {
  status: boolean;
  http_code: number;
  message: string;
  response_time: string;
  meta_data: string;
  data: {
    rows: Project[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    };
  };
}

export const coamAreaApi = {
  fetchCoamAreas: async (): Promise<CoamAreaResponse> => {
    const response = await apiClient.get<RegionApiResponse>("/region/details");
    const apiData = response.data;

    if (!apiData.status || !apiData.data) {
      throw new Error(apiData.message || "Failed to fetch region details");
    }

    return transformRegionDataToGeoJSON(apiData);
  },
  fetchQuickFilter: async (regionId: number): Promise<QuickFilterResponse> => {
    const response = await apiClient.get<QuickFilterResponse>(
      `/projects/quick-filter?region_id=${regionId}`
    );
    return response.data;
  },
  fetchWorkstackByProgramme: async (regionId: number): Promise<WorkstackResponse<WorkstackBreakdown>> => {
    const response = await apiClient.get<WorkstackResponse<WorkstackBreakdown>>(
      `/region/${regionId}/workstack`
    );
    return response.data;
  },
  fetchWorkstackByWorkstream: async (regionId: number): Promise<WorkstackResponse<WorkstreamBreakdown>> => {
    const response = await apiClient.get<WorkstackResponse<WorkstreamBreakdown>>(
      `/region/${regionId}/workstream`
    );
    return response.data;
  },
  fetchProjects: async (params: {
    stage_gate_id?: number;
    phase_type_id?: number;
    region_id?: number;
    phase_name?: string;
    my_tasks: boolean;
    page?: number;
    limit?: number;
  }): Promise<ProjectsResponse> => {
    const searchParams = new URLSearchParams();
 if (params.phase_name) {
      searchParams.append("phase_name", params.phase_name);
    }
    if (params.page) {
      searchParams.append("page", String(params.page));
    }
    if (params.limit) {
      searchParams.append("limit", String(params.limit));
    }
     if (params.phase_type_id) {
      searchParams.append("phase_type_id", String(params.phase_type_id));
    }
     if (params.my_tasks) {
      searchParams.append("assign_to_me", "true");
    }
    if (params.stage_gate_id) {
      searchParams.append("stage_gate_id", String(params.stage_gate_id));
    }
    if (params.region_id != null) {
    searchParams.append("region_id", String(params.region_id));
   }
   const response = await apiClient.get<ProjectsResponse>(
      `/projects?${searchParams.toString()}`
    );
    return response.data;
  },
};
